import React from 'react';
import { wrapFunctionalComponent } from 'utils/framework';
import LanguageLocaleUtils from 'utils/LanguageLocale';
import { Text, Flex } from 'components/ui';
import RedemptionInfo from 'components/Content/BeautyInsider/BeautyInsiderModules/RedemptionInfo/RedemptionInfo.f';
import { ACTIVITY_ICON_SRC, isGamificationActivity } from 'utils/BeautyInsider';
import EN from './locales/PointsActivityDetail_en_US';
import FR from './locales/PointsActivityDetail_fr_CA';

const { getLocalisedDataFromResource } = LanguageLocaleUtils;
const getText = getLocalisedDataFromResource(EN, FR);

const formatPointsDisplay = points => {
    return points && points > 0 ? `+${points} pts` : `${points} pts`;
};

const getAccessiblePointsLabel = pointValue => {
    const isEarned = pointValue > 0;

    return `Points ${isEarned ? 'earned' : 'redeemed'}: ${Math.abs(pointValue)} points`;
};

const renderDescriptionPointsSection = activity => {
    const pointsDisplay = formatPointsDisplay(activity.pointsUpdated);

    return (
        pointsDisplay && (
            <Flex
                direction='row'
                justifyContent='space-between'
                alignItems='flex-start'
                alignSelf='stretch'
                marginTop={12}
            >
                <Text
                    is='p'
                    children={activity.description1}
                />
                <Text
                    role='text'
                    aria-label={getAccessiblePointsLabel(activity.pointsUpdated)}
                >
                    {pointsDisplay}
                </Text>
            </Flex>
        )
    );
};

const renderSkuRedemptionSection = activity => {
    const { brandName, productName, skuImages } = activity.sku;
    const { targetUrl } = activity.sku.primaryProduct?.brand ?? {};
    const altText = activity.birthdayGiftReward ? activity.sku.skuImages?.altText : getAccessiblePointsLabel(activity.pointsUpdated);

    return (
        <RedemptionInfo
            title={activity.rougeReward ? activity.description1 : brandName}
            description={productName}
            skuImages={skuImages}
            link={
                activity.rougeReward || (activity.birthdayGiftReward && activity.pointsUpdated !== 0)
                    ? null
                    : { text: getText('brandLink'), url: targetUrl }
            }
            redemptionValue={
                activity.birthdayGiftReward && activity.pointsUpdated === 0 ? activity.description1 : formatPointsDisplay(activity.pointsUpdated)
            }
            accessibleRedemptionLabel={altText}
        />
    );
};

const renderPointsRedemptionSection = (src, activity) => {
    return (
        <RedemptionInfo
            src={src}
            title={activity.description1}
            description={activity.description2}
            redemptionValue={formatPointsDisplay(activity.pointsUpdated)}
            accessibleRedemptionLabel={getAccessiblePointsLabel(activity.pointsUpdated)}
        />
    );
};

const PointActivityDetail = ({ activity }) => {
    if (isGamificationActivity(activity.activityTypeCode)) {
        return renderPointsRedemptionSection(ACTIVITY_ICON_SRC.gamification, activity);
    }

    if (activity.cashBackReward) {
        return renderPointsRedemptionSection(ACTIVITY_ICON_SRC.cashApplied, activity);
    }

    if (activity.sku) {
        return renderSkuRedemptionSection(activity);
    }

    // DEFAULT: All other activity types
    // Handles: CREATE, EXTRA_POINT_PROMO_CREDIT, RETURN,
    // LOYALTY_POINTS_UPDATE, CANCEL, REVOKE, etc.
    return renderDescriptionPointsSection(activity);
};

export default wrapFunctionalComponent(PointActivityDetail, 'PointActivityDetail');
